/**
 * `<SourceDeleteDialog>` — the Acervo delete's confirm + the `document.delete`
 * call. Contract:
 * `ai-docs/handoffs/acervo-file-delete-feasibility-2026-08-01.md` — **§14 for the
 * CURRENT wording ruling** (§7 #9 is struck in place and still shows the
 * withdrawn sentence, deliberately, so the history stays legible) and §7-bis for
 * the misclick-guard exception.
 *
 * ## Why this is NOT `ConfirmDestructiveDialog`
 *
 * That component's entire friction IS a typed phrase ("no countdown timer /
 * delayed-enable — the phrase gate is the entire friction"), and the operator
 * ruled this feature a **misclick guard**: *"just to make sure the delete button
 * was not mistakenly clicked"*. A typed phrase cannot be reduced to that, so
 * reuse was impossible rather than merely undesirable — the small build cost of
 * a second dialog was accepted knowingly when both rulings were put to the
 * operator together.
 *
 * ⚠ The standing "destructive ops keep a typed-phrase confirm" preference is
 * **NOT revoked** — it still governs every other destructive op. This feature is
 * a named exception. Do not "restore consistency" here later; that is exactly
 * the drift-fix-undoes-a-ruling shape.
 *
 * ## The wording is ruled, verbatim — and it says NOTHING about recovery
 *
 * **`caseFiles.delete.consequence` = "removido do caso"** (en "removed from the
 * case", es "eliminado del caso"). It states the outcome and claims nothing in
 * either direction. That is not minimalism; it is the only position that
 * survived, because the sentence was ruled **twice in one day and was wrong
 * both times before this**:
 *
 *  - *"irreversível"* — false in the GRAVE direction, and counter-productive:
 *    a lawyer who believes junk is unrecoverable leaves the junk.
 *  - *"removido do caso — para recuperar, peça ao assistente"* — false in the
 *    REASSURING direction, which is the worse one because nobody files a bug
 *    saying "you reassured me too much". Blob bytes are **gitignored**, so the
 *    pre-delete checkpoint restores the metadata and the machine extraction but
 *    **never the evidence** (`git log --all` on a frozen original returns
 *    nothing), and no tool the assistant can call reaches a per-file bin.
 *
 * The operator then settled it upstream of the wording — *"we really should not
 * worry about reinstating a deleted file"* — so recovery is **not a product
 * goal** and the clause was DROPPED rather than reworded.
 *
 * ⚠ **Do not restore a recovery clause on wording grounds.** It needs a new
 * ruling AND an actor that can honour it; the last one had neither. And do not
 * swing back to "cannot be undone" either — the recycle bin exists as an
 * operator escape hatch, so that overstates in the opposite direction.
 *
 * ⚠ **Do not cite `lx_case_history` → `lx_case_restore` as this file's recovery
 * path.** They are GIT-based and these bytes are not in git. That claim lived in
 * this docblock after it had been measured false, which is exactly how the
 * wrong sentence survived review: **nothing compiles a comment.**
 *
 * ## The primary button is NOT auto-focused
 *
 * A misclick guard whose destructive button owns the initial focus is defeated
 * by one stray Enter — the guard would be decorative. Focus lands on Cancelar,
 * and Esc closes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogCancelBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useToastStore } from "@/stores/useToastStore";
import { useWorkspaceTreeSelectionStore } from "@/stores/useWorkspaceTreeSelectionStore";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { GatewayRpcError } from "@/lib/gateway-client";
import { deleteErrorKey } from "@/lib/workspace/source-delete";

export interface SourceDeleteDialogProps {
  /** Workspace-relative ref of the FROZEN ORIGINAL — the machine address of the
   *  op. Never shown (rule 6: the OS filename does not reach a lawyer). */
  path: string;
  /** FRIENDLY display name (title → alias → tag → name). This is what is shown. */
  displayName: string;
  /** `@handle`, when the source has one — rendered so the lawyer can see which
   *  reference is about to stop resolving. */
  tag?: string;
  /** The case this source belongs to. Case scope only — the org workspace has no
   *  case owner, so the ruling has no meaning there. */
  caseRef: { caseId: string; clientId?: string; matterId?: string };
  onClose: () => void;
}

export function SourceDeleteDialog({
  path,
  displayName,
  tag,
  caseRef,
  onClose,
}: SourceDeleteDialogProps) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc closes — but never mid-flight: the call is already at the runtime
      // and the dialog is the only thing that will report how it ended.
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const handleDelete = useCallback(async () => {
    if (!rpc || busy) return;
    setBusy(true);
    setError(null);
    try {
      // A caseId-only envelope is valid — the runtime recovers parentage from
      // the case model, so a LEAD inherited from a matter is not falsely denied.
      const res = await rpc.documentDelete({
        caseId: caseRef.caseId,
        ...(caseRef.clientId ? { clientId: caseRef.clientId } : {}),
        ...(caseRef.matterId ? { matterId: caseRef.matterId } : {}),
        path,
        confirmed: true,
      });
      // The row is gone — drop any state that still points AT it, or the tree
      // re-renders around a selection/viewer whose target no longer exists.
      const { selectedPath, clear } = useWorkspaceTreeSelectionStore.getState();
      if (selectedPath === path) clear();
      const fileStore = useWorkspaceFileStore.getState();
      if (fileStore.activeFile?.path === path) fileStore.closeFile();
      // The tree is the alias map's only source too (`useWorkspaceAliasMap`
      // derives from `useCaseWorkspaceTree` — no second fetch), so one
      // invalidation also re-resolves the handles the delete just retired.
      void queryClient.invalidateQueries({ queryKey: ["case-workspace-tree"] });
      // `reindexed:false` is NOT a failure — the files are gone either way. But
      // the catalog the ASSISTANT reads may still list the row, and the tree
      // (which reads disk) will look perfectly correct meanwhile. A plain
      // success toast there would be the reassuring half-truth this codebase
      // keeps shipping, so the partial state gets its own sentence.
      addToast({
        text: res.reindexed
          ? t("caseFiles.delete.done", { name: displayName })
          : t("caseFiles.delete.doneNotReindexed", { name: displayName }),
        kind: res.reindexed ? "success" : "warn",
        autoDismissMs: res.reindexed ? 4000 : 7000,
      });
      onClose();
    } catch (e) {
      // The CODE is the discriminator — the error envelope drops every other
      // key, and matching the runtime's English prose is what `deleteErrorKey`
      // exists to avoid. The dialog STAYS OPEN on failure: the file is still
      // there, and closing would read as success.
      const code = e instanceof GatewayRpcError ? e.code : undefined;
      setError(t(`caseFiles.delete.errors.${deleteErrorKey(code)}`));
      setBusy(false);
    }
  }, [rpc, busy, caseRef, path, queryClient, addToast, t, displayName, onClose]);

  const title = t("caseFiles.delete.title");

  return createPortal(
    // Backdrop click does NOT close (parity with the details dialog) and, more
    // to the point, must not be an accidental dismissal path for a guard.
    <div data-testid="source-delete-dialog" className={cn(dialogOverlayCls, busy && "cursor-wait")}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(dialogPanelCls, "max-w-md")}
      >
        <div className="flex items-start gap-2.5">
          <AlertTriangle
            size={16}
            aria-hidden
            className="mt-0.5 shrink-0 text-red-600 dark:text-red-400"
          />
          <h2 className={cn(dialogTitleCls, "min-w-0 flex-1")}>{title}</h2>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            aria-label={t("actions.close")}
            className={cn(dialogCloseBtnCls, "disabled:opacity-50")}
          >
            <X size={16} />
          </button>
        </div>

        {/* WHICH file — the friendly name, plus the handle when there is one, so
            the lawyer can see which `@reference` is about to stop resolving.
            Never the OS filename. */}
        <p
          data-testid="source-delete-name"
          className="mt-3 break-words text-body-sm font-medium text-coffee-800 dark:text-cream-100"
        >
          {displayName}
        </p>
        {tag && (
          <p
            data-testid="source-delete-handle"
            className="mt-0.5 truncate font-mono text-[11px] text-coffee-500 dark:text-cream-400"
          >
            @{tag}
          </p>
        )}

        {/* The ruled consequence line. Never "irreversible" — see the docblock. */}
        <p
          data-testid="source-delete-consequence"
          className="mt-3 text-body-sm text-coffee-600 dark:text-cream-300"
        >
          {t("caseFiles.delete.consequence")}
        </p>

        {error && (
          <p
            data-testid="source-delete-error"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-2.5 text-[12px] text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="source-delete-cancel"
            className={dialogCancelBtnCls}
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            // `!rpc` (gateway down) DISABLES rather than silently no-opping.
            // The sibling dialogs guard with an early `return`, which on a
            // destructive confirm is the failure-that-resolves shape wearing its
            // worst face: the lawyer clicks Remover, nothing happens, and they
            // cannot tell whether the file went. An inert-looking button says so.
            disabled={busy || !rpc}
            data-testid="source-delete-confirm"
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-body-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 size={12} aria-hidden className="animate-spin" />}
            {t("caseFiles.delete.confirmAction")}
          </button>
        </div>
      </div>
    </div>,
    // Portalled to <body>: a `position: fixed` overlay is only viewport-relative
    // while no ancestor establishes a containing block, and the side pane has
    // trapped a modal that way before (`content-visibility` implies paint
    // containment). Same defence the details dialog now carries.
    document.body,
  );
}
